/* ═══════════════════════════════════════════════════════════════════════════
   LYKKE SALONG — the business a future tenant is shown

   Every other business on this platform seeds a thin demonstration: two past
   visits so a customer's history is not empty, a sample day in the diary.
   That is enough to prove a screen draws. It is not enough to show somebody
   what running their salon here would actually be like, and Lykke Salong is
   the one we put in front of people.

   So this file is a fuller picture: a fortnight of appointments still to
   come and three months of completed work behind them, with the things a
   salon owner opens a console to look at —

     what the customer booked, and how they chose to pay for it
     who carried it out
     what went over the counter with it
     what it came to
     and the reference the sale is known by for ever after

   ── Why the references matter ────────────────────────────────────────────

   Bokføringsloven §§ 4 nr. 7 and 6 want a trail that runs both ways: from a
   document up to the reported figure, and from the figure back down to the
   document. A sequential reference is what makes the second direction
   possible, so every completed sale here carries one and the series is
   unbroken — `gaps()` in domain/salesdoc.js would report it otherwise, and
   the screen shows what it finds.

   ── Everything here is made up, and says so ──────────────────────────────

   These are not real customers and not real takings. Every record carries
   `demo: true`, every screen that counts them says how many, and the money
   is deliberately excluded from the takings and the journal — a business
   that found this file's figures inside its own revenue would be right to
   distrust every other number in the console.

   Everything a screen shows is in English, like the rest of this build. The
   exceptions are the ones the whole project keeps: the business's name, its
   address, and the entries on its own price list — Klipp, Farge & striper —
   because those are what the salon calls them and translating a tenant's own
   price list would be inventing a service they do not sell.

   The names are ordinary Norwegian ones and belong to nobody. The
   organisation number is the one thing NOT invented: Lykke Salong has none
   on this platform, and a plausible nine digits would be worse than a blank,
   because somebody would read it as checked.
   ═══════════════════════════════════════════════════════════════════════ */

export const BUSINESS = 'lykke';

/* The people, by the ids `peopleAt('lykke')` hands out. Written here rather
   than looked up so the spread of work between them is deliberate — Ida
   takes the colour work, which is what a fargespesialist is for. */
const MARTE = 'lykke-1';
const IDA = 'lykke-2';
const OSKAR = 'lykke-3';

/* Customers. Two of them come back, which is the point of a salon and the
   reason the history is worth opening: the third visit tells you something
   the first cannot. */
export const CUSTOMERS = [
  /* Not Anna Bergland, deliberately. That is the name behind the platform's
     Vipps demonstration identity, and a salon customer sharing it would make
     "the business never sees the account behind a number" impossible to tell
     apart from "she is on the list because she is a customer" — which is the
     one rule on this platform that must stay checkable. */
  { phone: '41234567', name: 'Elin Dahl' },
  { phone: '92233445', name: 'Kari Nordmann' },
  { phone: '48899001', name: 'Henrik Aas' },
  { phone: '45566778', name: 'Sofie Berg' },
  { phone: '90011223', name: 'Jonas Lie' },
  { phone: '47788990', name: 'Live Haugen' },
];

/* A treatment, priced from the salon's own list. The names match
   `BUSINESSES.find(b => b.id === 'lykke').services`, because a demo that
   invented a service would put a name on the diary that is on no price
   list — and then the takings screen would group by something the tenant
   cannot find. */
const KLIPP = ['Klipp', 590, 45];
const FARGE = ['Farge & striper', 1490, 120];
const STYLING = ['Styling', 890, 60];
const BARBER = ['Barber', 490, 45];

/* Products, by the ids in `productsFor('hair')`. A salon's counter is a real
   part of its takings and the thing most booking systems leave out. */
const P = {
  shampoo: ['hair-shampoo', 'Shampoo, 300 ml', 329],
  condition: ['hair-condition', 'Conditioner, 300 ml', 329],
  mask: ['hair-mask', 'Treatment mask, 200 ml', 449],
  oil: ['hair-oil', 'Hair oil, 100 ml', 389],
  spray: ['hair-spray', 'Heat protection, 150 ml', 279],
  silver: ['hair-silver', 'Silver shampoo, 250 ml', 349],
};

const prod = (key, qty = 1) => ({
  id: P[key][0], name: P[key][1], price: P[key][2], qty,
});

/* ── Still to come ────────────────────────────────────────────────────────
   Days ahead of today, so the fortnight stays a fortnight whenever anybody
   opens it. Every one carries the payment method the customer CHOSE at
   booking — which is a fact about the booking and not about money that has
   moved, and the screens keep those two apart.

   `held` means the amount was authorised against the card or in Vipps when
   they booked. It is still the customer's money until the work is done. */
export const AHEAD = [
  { in: 1, at: '09:00', who: 0, with: MARTE, svc: KLIPP, pay: 'Vipps', held: true,
    note: 'A little shorter than last time.' },
  { in: 1, at: '11:00', who: 3, with: IDA, svc: FARGE, pay: 'Card', held: true,
    note: 'Wants the same colour as in June.' },
  { in: 2, at: '10:30', who: 2, with: OSKAR, svc: BARBER, pay: 'Vipps', held: true },
  { in: 3, at: '14:00', who: 1, with: MARTE, svc: KLIPP, pay: 'Card', held: false,
    note: 'Paying at the counter.' },
  { in: 4, at: '12:00', who: 4, with: IDA, svc: FARGE, pay: 'Vipps', held: true,
    /* Pre-ordered over the counter — a salon's counter is part of the
       booking as often as it is an afterthought, and a system that cannot
       record it loses the sale. */
    products: [prod('silver')], note: 'Taking the silver shampoo with her.' },
  { in: 6, at: '15:30', who: 5, with: MARTE, svc: STYLING, pay: 'Card', held: true,
    note: 'Wedding the Saturday after.' },
  { in: 8, at: '09:30', who: 0, with: IDA, svc: FARGE, pay: 'Vipps', held: true },
  { in: 11, at: '13:00', who: 2, with: OSKAR, svc: KLIPP, pay: 'Vipps', held: true },
  { in: 13, at: '16:00', who: 3, with: MARTE, svc: KLIPP, pay: 'Card', held: true },
];

/* ── What has already happened ────────────────────────────────────────────
   Days BACK from today, newest first, and the reference numbers run in the
   opposite order so that the oldest sale carries the lowest number — which
   is what a series means and what `gaps()` checks.

   `seq` is the position in the year's series. They are consecutive on
   purpose: a hole here would show up on the report as a hole, which is
   exactly what the report is for, and a demonstration that shipped with one
   would teach the wrong lesson about what a clean set of books looks like. */
export const BEHIND = [
  { ago: 3, at: '10:00', who: 0, with: MARTE, svc: KLIPP, pay: 'Vipps', seq: 147,
    products: [prod('shampoo')] },
  { ago: 4, at: '11:30', who: 3, with: IDA, svc: FARGE, pay: 'Card', seq: 146,
    products: [prod('silver'), prod('mask')] },
  { ago: 4, at: '15:00', who: 2, with: OSKAR, svc: BARBER, pay: 'Vipps', seq: 145 },
  { ago: 8, at: '09:30', who: 1, with: MARTE, svc: KLIPP, pay: 'Card', seq: 144 },
  { ago: 9, at: '13:30', who: 5, with: IDA, svc: FARGE, pay: 'Vipps', seq: 143,
    products: [prod('oil')] },
  { ago: 12, at: '10:00', who: 4, with: MARTE, svc: STYLING, pay: 'Card', seq: 142 },
  { ago: 16, at: '14:30', who: 0, with: IDA, svc: FARGE, pay: 'Vipps', seq: 141,
    products: [prod('mask'), prod('spray')] },
  { ago: 19, at: '11:00', who: 2, with: OSKAR, svc: KLIPP, pay: 'Cash', seq: 140 },
  { ago: 23, at: '16:00', who: 3, with: MARTE, svc: KLIPP, pay: 'Vipps', seq: 139,
    products: [prod('condition')] },
  { ago: 27, at: '09:00', who: 1, with: IDA, svc: FARGE, pay: 'Card', seq: 138 },
  { ago: 31, at: '12:30', who: 5, with: MARTE, svc: KLIPP, pay: 'Vipps', seq: 137,
    products: [prod('shampoo'), prod('condition')] },
  { ago: 38, at: '15:30', who: 4, with: OSKAR, svc: BARBER, pay: 'Card', seq: 136 },
  { ago: 44, at: '10:30', who: 0, with: MARTE, svc: KLIPP, pay: 'Vipps', seq: 135 },
  { ago: 51, at: '13:00', who: 2, with: IDA, svc: FARGE, pay: 'Card', seq: 134,
    products: [prod('silver')] },
  { ago: 58, at: '11:30', who: 3, with: MARTE, svc: STYLING, pay: 'Vipps', seq: 133 },
  { ago: 66, at: '14:00', who: 1, with: OSKAR, svc: KLIPP, pay: 'Card', seq: 132,
    products: [prod('spray')] },
  { ago: 74, at: '09:30', who: 5, with: IDA, svc: FARGE, pay: 'Vipps', seq: 131 },
  { ago: 81, at: '16:30', who: 4, with: MARTE, svc: KLIPP, pay: 'Card', seq: 130,
    products: [prod('oil')] },
];

export const SERVICE_VAT = 'standard';
export const PRODUCT_VAT = 'standard';

/* Said on every screen that shows any of this. */
export const WHAT = 'Lykke Salong is the demonstration business. Its '
  + 'appointments, customers and sales are made up so a future tenant can see '
  + 'a working salon rather than an empty console. None of it is counted as '
  + 'anybody\'s real revenue.';
